import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, combineLatest } from 'rxjs';
import { map, mergeMap, tap, filter, take } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { UserDto } from '@bo-client/shared/models/userDto';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { constants } from '@bo-client/shared/constants/constans';
import { uri } from '@bo-client/shared/constants/uri';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { ConfigService } from '@bo-client/config/config.service';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { Router, ActivatedRoute } from '@angular/router';
import { combineLatestWith, distinctUntilChanged } from 'rxjs/operators';

export interface Credentials {
  // Customize received credentials here
  username: string;
  token: string;
}

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

export interface ExternalTokenLoginContext {
  external_token: string;
  grant_type: string;
}

const credentialsKey = 'credentials';

/**
 * Provides a base for authentication workflow.
 * The Credentials interface as well as login/logout methods should be replaced with proper implementation.
 */
@Injectable()
export class AuthenticationService {
  public isLoggedIn$: Observable<boolean>;
  private _isLoggedIn$: BehaviorSubject<boolean>;
  private sources = [];

  constructor(
    private http: HttpClient,
    private httpHapService: HttpHapService,
    private dataShareService: DataShareService,
    private configService: ConfigService,
    private router: Router,
    private authorizationService: AuthorizationService,
    private activatedRoute: ActivatedRoute
  ) {
    const savedCredentials = sessionStorage.getItem(credentialsKey) || localStorage.getItem(credentialsKey);
    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials);
    }
    this._isLoggedIn$ = new BehaviorSubject(savedCredentials != null);
    this.isLoggedIn$ = this._isLoggedIn$.asObservable();
  }

  private _credentials: Credentials | null;

  /**
   * Gets the user credentials.
   * @return {Credentials} The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    return this._credentials;
  }

  get isLoggedIn(): boolean {
    return this._isLoggedIn$.getValue();
  }

  /**
   * Authenticates the user.
   * @param {LoginContext} context The login parameters.
   * @return {Observable<Credentials>} The user credentials.
   */
  login(context: LoginContext): Observable<any> {
    localStorage.clear();

    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Basic ' + this.configService.getSettings('basicAuth'));
    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

    let params = new HttpParams({
      encoder: {
        encodeKey(key: string): string {
          return key;
        },
        encodeValue(value: string): string {
          return encodeURIComponent(value);
        },
        decodeKey(key: string): string {
          return key;
        },
        decodeValue(value: string): string {
          return decodeURIComponent(value);
        }
      } as HttpUrlEncodingCodec
    });

    params = params.set('username', context.username);
    params = params.set('password', context.password);
    params = params.set('grant_type', 'password');

    // first user login
    const userAuthentication$ = this.http
      .post(this.configService.getSettings('tokenEndpoint'), params, {
        headers: headers
      })
      .pipe(
        map((data: any) => {
          const creds: Credentials = {
            username: context.username,
            token: data.access_token
          };
          this.setCredentials(creds);
          this._isLoggedIn$.next(true);
        })
      );
    return userAuthentication$.pipe(mergeMap(() => this.loadUserData()));
  }

  /**
   * Authenticates the user using an external IDP token.
   */
  loginWithExternalToken(context: ExternalTokenLoginContext): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Basic ' + this.configService.getSettings('basicAuth'));
    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

    let params = new HttpParams({
      encoder: new HttpUrlEncodingCodec()
    });

    params = params.set('external_token', context.external_token);
    params = params.set('grant_type', context.grant_type);

    return this.http
      .post(this.configService.getSettings('tokenEndpoint'), params, {
        headers: headers
      })
      .pipe(
        map((data: any) => {
          const creds: Credentials = {
            username: data.username || '',
            token: data.access_token
          };
          this.setCredentials(creds);
          this._isLoggedIn$.next(true);
        }),
        switchMap(() => this.loadUserData()),
        switchMap(() => {
          // Warte auf Tenant-Konfiguration, bevor navigiert wird
          return this.dataShareService.tenantConfig$.pipe(
            filter((config) => !!config && !!Object.keys(config).length),
            take(1),
            combineLatestWith(this.authorizationService.selectRouteLogin$),
            tap(([config, route]) => {
              // Prüfe, ob eine gespeicherte Redirect-URL vorhanden ist
              const redirectUrl = sessionStorage.getItem('redirectUrl');
              if (redirectUrl) {
                sessionStorage.removeItem('redirectUrl');
                this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
              } else {
                this.router.navigate([route], {
                  replaceUrl: true
                });
              }
            })
          );
        })
      );
  }

  loadUserData(): Observable<UserDto> {
    // Hole tenantId aus Route-Parametern (queryParams oder paramMap) als Observable
    const queryTenantId$ = this.activatedRoute.queryParams.pipe(map((params) => params['tenantId'] || null));
    const paramTenantId$ = this.activatedRoute.paramMap.pipe(map((params) => params.get('tenantId') || null));

    const tenantId$ = combineLatest([queryTenantId$, paramTenantId$]).pipe(
      map(([queryTenantId, paramTenantId]) => queryTenantId || paramTenantId || null)
    );

    return tenantId$.pipe(
      distinctUntilChanged(),
      switchMap((tenantId) => {
        return this.httpHapService.get(uri.getUserMe).pipe(
          tap((userDto: UserDto) => {
            this.dataShareService.setUserData(userDto, tenantId);
          })
        );
      })
    );
  }

  /**
   * Logs out the user and clear credentials.
   * @return {Observable<boolean>} True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    // Customize credentials invalidation here
    this.clearCredentials();
    this._credentials = null;
    this._isLoggedIn$.next(false);
    return of(true);
  }

  /**
   * Checks is the user is authenticated.
   * @return {boolean} True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.credentials;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param {Credentials=} credentials The user credentials.
   * @param {boolean=} remember True to remember credentials across sessions.
   */
  private setCredentials(credentials?: Credentials, remember?: boolean) {
    this._credentials = credentials || null;
    if (credentials) {
      localStorage.setItem(constants.storageCredentials, JSON.stringify(credentials));
    } else {
      localStorage.removeItem(constants.storageCredentials);
    }
  }

  private clearCredentials() {
    localStorage.clear();
    sessionStorage.clear();
  }
}
